import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Statistic, Typography, message } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import { formatCents } from "../shared/money";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";
import StatementDialog from "./StatementDialog";
import { DownloadButton, useLabels, useRange } from "./receivablesUi";

const days = v => (v === null || v === undefined ? "–" : v);

/** RP-5: DSO and payment behaviour per customer with prepayment / dunning-block hints. */
const PaymentBehaviourTab = () => {
  const { t } = useTranslation("accounting");
  const labels = useLabels();
  const { params, picker } = useRange([dayjs().subtract(12, "month"), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .getPaymentBehaviour(params)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [params]);

  const th = data?.thresholds || {};
  return (
    <div>
      <PageToolbar
        filters={picker}
        actions={
          <DownloadButton
            label={t("Download CSV")}
            promise={() => api.downloadPaymentBehaviour(params)}
            filename={`Zahlungsverhalten_${params.from}_${params.to}.csv`}
          />
        }
      />
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("DSO (days)")} value={days(data?.totals?.dso)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Average days to pay")} value={days(data?.totals?.avgDaysToPay)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Open")} value={formatCents(data?.totals?.open || 0)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Customers with hints")} value={data?.totals?.flagged || 0} />
          </Card>
        </Col>
      </Row>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        rowKey={r => String(r.customer)}
        dataSource={data?.customers || []}
        columns={[
          { title: t("Customer number"), dataIndex: "customerNumber", width: 110 },
          { title: t("Customer"), dataIndex: "name" },
          { title: t("Invoices"), dataIndex: "invoices", align: "end" },
          {
            title: t("Invoiced (gross)"),
            dataIndex: "invoiced",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Average days to pay"),
            dataIndex: "avgDaysToPay",
            align: "end",
            render: days
          },
          { title: t("Days past due"), dataIndex: "avgDaysLate", align: "end", render: days },
          { title: t("Open"), dataIndex: "open", align: "end", render: v => <Amount cents={v} /> },
          { title: t("Dunning letters"), dataIndex: "dunningLetters", align: "end" },
          { title: t("Hints"), dataIndex: "flags", render: labels.flagTags },
          {
            title: "",
            key: "actions",
            render: (_, r) =>
              r.customer ? (
                <Button size="small" onClick={() => setStatement({ customerId: r.customer })}>
                  {t("Statement")}
                </Button>
              ) : null
          }
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        {t(
          "Prepayment is suggested when a customer takes more than {{days}} days on average, received {{letters}} or more dunning letters of stage 2 or higher, or had a receivable written off.",
          { days: th.prepaymentDays ?? 60, letters: th.stage2Letters ?? 2 }
        )}
      </Typography.Paragraph>
      <StatementDialog
        open={Boolean(statement)}
        customerId={statement?.customerId}
        from={params.from}
        to={params.to}
        onClose={() => setStatement(null)}
      />
    </div>
  );
};

export default PaymentBehaviourTab;
